import AudiotrackRoundedIcon from "@mui/icons-material/AudiotrackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import {
  chatAttachmentKind,
  formatBytes,
  isVoiceNoteAttachment,
  type ChatAttachment,
} from "@xop/dashboard-shared";
import { useState } from "react";

import { downloadAttachment, useAttachmentObjectUrl } from "../../lib/chat/attachments.js";
import { VoiceMessageBubble } from "./VoiceMessageBubble.js";

/**
 * Renders a message attachment INLINE — chat plays its own media, it never
 * hands a file off to a new browser tab:
 *   • voice note → a WhatsApp-style waveform bubble (play / scrub / speed).
 *   • image → thumbnail; clicking opens a full-size in-app lightbox (a Modal).
 *   • video → an inline `<video controls>` player.
 *   • audio FILE → an inline `<audio controls>` player.
 *   • anything else → a download chip.
 * Every byte is fetched through the bearer-authenticated endpoint and exposed
 * as a local object URL — there is no public URL, and a `<video>`/`<audio>`/
 * `<img>` element can't execute a malicious upload.
 */
export function ChatAttachmentView({
  attachment,
  mine,
}: {
  attachment: ChatAttachment;
  mine: boolean;
}): JSX.Element {
  // A recorded voice message renders as the waveform bubble — checked BEFORE
  // the kind switch, since a voice note is also playable audio.
  if (isVoiceNoteAttachment(attachment)) {
    return <VoiceMessageBubble attachment={attachment} mine={mine} />;
  }
  switch (chatAttachmentKind(attachment.mime)) {
    case "image":
      return <ImageAttachment attachment={attachment} />;
    case "video":
      return <VideoAttachment attachment={attachment} />;
    case "audio":
      return <AudioAttachment attachment={attachment} />;
    default:
      return <FileAttachment attachment={attachment} mine={mine} />;
  }
}

function ImageAttachment({ attachment }: { attachment: ChatAttachment }): JSX.Element {
  const url = useAttachmentObjectUrl(attachment.id);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        data-testid="chat-image-attachment"
        sx={{
          width: 240,
          maxWidth: "100%",
          height: url ? "auto" : 140,
          borderRadius: 1.5,
          overflow: "hidden",
          mb: 0.5,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: url ? "zoom-in" : "default",
        }}
        onClick={() => url && setOpen(true)}
      >
        {url ? (
          <Box
            component="img"
            src={url}
            alt={attachment.filename}
            sx={{ display: "block", width: "100%", maxHeight: 320, objectFit: "cover" }}
          />
        ) : (
          <CircularProgress size={22} />
        )}
      </Box>

      {/* In-app full-size viewer — NOT a new browser tab. */}
      <Modal
        open={open && !!url}
        onClose={() => setOpen(false)}
        data-testid="chat-image-lightbox"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
      >
        <Box sx={{ position: "relative", outline: "none", maxWidth: "92vw", maxHeight: "92vh" }}>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close image"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Box
            component="img"
            src={url ?? ""}
            alt={attachment.filename}
            sx={{ display: "block", maxWidth: "92vw", maxHeight: "92vh", borderRadius: 1 }}
          />
        </Box>
      </Modal>
    </>
  );
}

function VideoAttachment({ attachment }: { attachment: ChatAttachment }): JSX.Element {
  const url = useAttachmentObjectUrl(attachment.id, attachment.mime);
  return (
    <Box
      data-testid="chat-video-attachment"
      sx={{
        width: 300,
        maxWidth: "100%",
        mb: 0.5,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: url ? "auto" : 168,
      }}
    >
      {url ? (
        <Box
          component="video"
          src={url}
          controls
          preload="metadata"
          sx={{ display: "block", width: "100%", maxHeight: 360 }}
        />
      ) : (
        <CircularProgress size={22} sx={{ color: "#fff" }} />
      )}
    </Box>
  );
}

function AudioAttachment({ attachment }: { attachment: ChatAttachment }): JSX.Element {
  const url = useAttachmentObjectUrl(attachment.id, attachment.mime);
  return (
    <Box
      data-testid="chat-audio-attachment"
      sx={{ mb: 0.5, p: 1, borderRadius: 1.5, bgcolor: "action.hover", minWidth: 240, maxWidth: 340 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, minWidth: 0 }}>
        <AudiotrackRoundedIcon sx={{ color: "chat.accent", fontSize: 18, flexShrink: 0 }} />
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, maxWidth: 280 }}>
          {attachment.filename}
        </Typography>
      </Box>
      {url ? (
        <Box component="audio" src={url} controls sx={{ width: "100%", display: "block" }} />
      ) : (
        <CircularProgress size={18} />
      )}
    </Box>
  );
}

function FileAttachment({
  attachment,
  mine,
}: {
  attachment: ChatAttachment;
  mine: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  async function onDownload(): Promise<void> {
    setBusy(true);
    try {
      await downloadAttachment(attachment);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        p: 1,
        borderRadius: 1.5,
        bgcolor: mine ? "rgba(0,0,0,0.06)" : "action.hover",
        minWidth: 200,
      }}
    >
      <InsertDriveFileOutlinedIcon sx={{ color: "chat.accent" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, maxWidth: 220 }}>
          {attachment.filename}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "chat.meta" }}>
          {formatBytes(attachment.size_bytes)}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={onDownload}
        disabled={busy}
        aria-label={`Download ${attachment.filename}`}
      >
        {busy ? <CircularProgress size={16} /> : <DownloadRoundedIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}
